/**
 * useProfessionalProfileFormTour
 * Tour for the professional profile form (when editing own professional profile)
 */

import { Tour } from '@shared/lib/tours/types'
import { TOUR_THEME } from '@shared/lib/tours/theme'
import { useCallback, useEffect } from 'react'
import { usePageReadyForTour } from './usePageReadyForTour'
import { useTrackedDriverTour } from './useTrackedDriverTour'
import { markTourAsCompleted, useTourManager } from './useTourManager'

const TOUR_NAME = 'professional-profile-form'

export function useProfessionalProfileFormTour(autoStart: boolean = false): Tour {
  const { isTourCompleted, shouldShowTour, resetTour } = useTourManager()
  const isPageReady = usePageReadyForTour()
  const startTrackedTour = useTrackedDriverTour()

  const startTour = useCallback(
    (delayMs = 0) => {
      return startTrackedTour(
        {
          ...TOUR_THEME,
          showProgress: true,
          nextBtnText: 'Siguiente',
          prevBtnText: 'Anterior',
          doneBtnText: '¡Listo!',
          progressText: '{{current}} de {{total}}',

          onDestroyed: () => {
            markTourAsCompleted(TOUR_NAME)
          },

          steps: [
            {
              element: '[data-tour="professional-form-picture"]',
              popover: {
                title: 'Foto de Perfil',
                description:
                  'Subí una foto profesional para que los clientes puedan verte. Una buena foto genera confianza.',
                side: 'bottom',
              },
            },
            {
              element: '[data-tour="professional-form-basic"]',
              popover: {
                title: 'Información Básica',
                description:
                  'Completá tu nombre profesional, profesión, ciudad y una descripción de tus servicios.',
                side: 'bottom',
              },
            },
            {
              element: '[data-tour="professional-form-contact"]',
              popover: {
                title: 'Contacto',
                description:
                  'Ingresá tu teléfono de contacto. Los clientes podrán comunicarse contigo por WhatsApp o llamada.',
                side: 'top',
              },
            },
            {
              element: '[data-tour="professional-form-social"]',
              popover: {
                title: 'Redes Sociales',
                description:
                  'Agregá tus redes sociales y ubicación en Google Maps para que los clientes te encuentren más fácilmente.',
                side: 'top',
              },
            },
          ],
        },
        { delayMs }
      )
    },
    [startTrackedTour]
  )

  // Auto-start tour on mount if conditions are met
  useEffect(() => {
    // Only start tour when page is ready (loaded and no PWA prompt)
    if (!isPageReady) return

    if (autoStart && shouldShowTour(TOUR_NAME)) {
      return startTour(300)
    }
  }, [autoStart, isPageReady, shouldShowTour, startTour])

  const reset = () => {
    resetTour(TOUR_NAME)
  }

  return {
    startTour,
    isCompleted: isTourCompleted(TOUR_NAME),
    reset,
  }
}
